import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

export interface ImageStyle {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface ImageStyleSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const ImageStyleSelect: React.FC<ImageStyleSelectProps> = ({
  value,
  onChange,
  className = ''
}) => {
  if (process.env.NODE_ENV === 'development') {
    logger.debug('ImageStyleSelect', 'Rendering', { value });
  }
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  // Log when dropdown opens/closes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('ImageStyleSelect', 'Dropdown state', { isOpen });
    }
  }, [isOpen]);
  
  const imageStyles: ImageStyle[] = [
    {
      id: 'auto',
      name: t('contentCreation.form.imageStyles.auto'),
      emoji: '🤖',
      description: t('contentCreation.form.imageStyleDescriptions.auto')
    },
    {
      id: 'realistic',
      name: t('contentCreation.form.imageStyles.realistic'),
      emoji: '📷',
      description: t('contentCreation.form.imageStyleDescriptions.realistic')
    },
    {
      id: 'design',
      name: t('contentCreation.form.imageStyles.design'),
      emoji: '✏️',
      description: t('contentCreation.form.imageStyleDescriptions.design')
    },
    {
      id: '3d',
      name: t('contentCreation.form.imageStyles.3d'),
      emoji: '🎯',
      description: t('contentCreation.form.imageStyleDescriptions.3d')
    },
    {
      id: 'artistic',
      name: t('contentCreation.form.imageStyles.artistic'),
      emoji: '🎨',
      description: t('contentCreation.form.imageStyleDescriptions.artistic')
    },
    {
      id: 'modern',
      name: t('contentCreation.form.imageStyles.modern'),
      emoji: '✨',
      description: t('contentCreation.form.imageStyleDescriptions.modern')
    },
    {
      id: 'ad',
      name: t('contentCreation.form.imageStyles.ad'),
      emoji: '📢',
      description: t('contentCreation.form.imageStyleDescriptions.ad')
    }
  ];
  
  const selectedStyle = imageStyles.find(style => style.id === value) || imageStyles[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation(); // Prevent event from bubbling up
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left ${className}`}
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{selectedStyle.emoji}</span>
          <span className="text-gray-700">{selectedStyle.name}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation(); // Prevent event from bubbling up
              setIsOpen(false);
            }}
          />
          <div className="absolute left-0 right-0 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1">
              {imageStyles.map(style => (
                <button
                  type="button"
                  key={style.id}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event from bubbling up
                    if (process.env.NODE_ENV === 'development') {
                      logger.debug('ImageStyleSelect', 'Style selected', style.id);
                    }
                    onChange(style.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 ${
                    style.id === value
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{style.emoji}</span>
                    <div>
                      <div className="font-medium">{style.name}</div>
                      <div className="text-xs text-gray-500">{style.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 